import { useState } from 'react'
import type { WireColor } from '@/api/types/color'

const RECENTLY_USED_KEY = 'larry.recently-used-colors.v1'
const MAX_RECENTLY_USED = 10

type RecentEntry = { colorId: number; tint: number }

/** A recently used color at the tint it was applied with; the same color at two tints is two entries. */
export type RecentColor = { color: WireColor; tint: number }

/** Reads stored entries; a bare number is an entry saved before tints were kept, at 100%. */
function readStoredEntries(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENTLY_USED_KEY)

    if (!raw) return []

    const parsed: unknown = JSON.parse(raw)

    if (!Array.isArray(parsed)) return []

    return parsed.flatMap((entry: unknown): RecentEntry[] => {
      if (typeof entry === 'number') return [{ colorId: entry, tint: 100 }]

      if (
        typeof entry === 'object' &&
        entry != null &&
        typeof (entry as RecentEntry).colorId === 'number' &&
        typeof (entry as RecentEntry).tint === 'number'
      ) {
        return [{ colorId: (entry as RecentEntry).colorId, tint: (entry as RecentEntry).tint }]
      }

      return []
    })
  } catch {
    return []
  }
}

export function useRecentlyUsedColors(allColors: WireColor[]) {
  const [recentEntries, setRecentEntries] = useState<RecentEntry[]>(readStoredEntries)

  const recentColors = recentEntries.flatMap((entry): RecentColor[] => {
    const color = allColors.find((c) => c.colorId === entry.colorId)

    return color ? [{ color, tint: entry.tint }] : []
  })

  function addRecentColor(colorId: number, tint = 100) {
    const updated = [
      { colorId, tint },
      ...recentEntries.filter((e) => e.colorId !== colorId || e.tint !== tint),
    ].slice(0, MAX_RECENTLY_USED)

    setRecentEntries(updated)

    try {
      localStorage.setItem(RECENTLY_USED_KEY, JSON.stringify(updated))
    } catch {
      // storage quota exceeded or private browsing — silently ignore
    }
  }

  return { recentColors, addRecentColor }
}
